/**
 * Database panel for the workspace.
 *
 * Surfaces the project's own database for inspection (NOT mutation):
 *  - Connection metadata from `GET /projects/:id/database` (host/db/user +
 *    the masked URL, with a copy button for the masked URL only).
 *  - A table list from `GET /projects/:id/database/tables`; clicking a table
 *    runs `SELECT * … LIMIT 100`. Connection info and the table list load
 *    INDEPENDENTLY: when one fails the other still renders, with a scoped
 *    inline error + retry for the failed part.
 *  - A read-only SQL console: a textarea + Run → `POST /projects/:id/database/query`
 *    → results grid, with the `truncated` flag surfaced and server errors
 *    (e.g. a read-only / write-attempt violation) shown clearly.
 *
 * Read-only is enforced server-side at the transaction level; this panel makes
 * that obvious in the UI. Pure result/url shaping lives in
 * `utilities/ops-utilities.ts` so it can be unit-tested without a DOM.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Icon, Tooltip } from '@molecule/app-ui-react'

import { useIsMobile } from '../hooks/useMediaQuery.js'
import {
  columnTypeMap,
  databaseEmptyStateKey,
  type DatabaseInfo,
  databaseRequest,
  displayMaskedUrl,
  isInsufficientRoleError,
  type OpsEnv,
  prettyCellValue,
  queryRequest,
  type QueryResult,
  type ResultGrid,
  rowCapNotice,
  shapeResultGrid,
  type TableInfo,
  tablePreviewQuery,
  tablesRequest,
  type TablesResponse,
} from '../utilities/ops-utilities.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'

/** Props for the Database panel. */
interface DatabasePanelProps {
  /** Project whose database to inspect. */
  projectId: string
  /**
   * Renders without the outer `padding: 16` wrapper — for embedding inside an
   * already-padded container (the Storage panel's database row). Every mol-id,
   * including the root `database-panel`, is unchanged.
   */
  embedded?: boolean
}

/** Extracts the server-provided error message + stable key from an HttpError. */
function serverError(err: unknown): { message: string | null; errorKey: string | null } {
  const data = (err as { response?: { data?: { error?: string; errorKey?: string } } })?.response
    ?.data
  return {
    message: typeof data?.error === 'string' ? data.error : null,
    errorKey: typeof data?.errorKey === 'string' ? data.errorKey : null,
  }
}

// Selectable-pill treatment shared by the env toggle AND the table buttons, so
// every active state in the panel is driven by the SAME `--color-primary` theme
// token (IDE7 fixed two divergent hardcoded accents: a blue `#4070e0` env pill
// and an indigo `#6366f1` table pill). `color-mix` keeps the tint/border in one
// token — never a second hardcoded hex. Exported for the Storage panel's
// cache-key / search-index pills so every explorer shares ONE pill treatment.
export const ACTIVE_PILL: React.CSSProperties = {
  border: '1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)',
  background: 'color-mix(in srgb, var(--color-primary) 15%, transparent)',
  color: 'var(--color-primary)',
  // A pill label never wraps — rows wrap whole pills instead.
  whiteSpace: 'nowrap',
}
/** The unselected counterpart of {@link ACTIVE_PILL}. */
export const INACTIVE_PILL: React.CSSProperties = {
  border: '1px solid var(--color-border-secondary)',
  background: 'transparent',
  color: 'inherit',
  whiteSpace: 'nowrap',
}

/** A single grid cell opened in the click-to-expand detail popover. */
interface CellDetail {
  /** The column the cell belongs to (popover header). */
  column: string
  /** The raw formatted cell string from the grid. */
  raw: string
  /** Trigger rect (viewport coords) so the popover anchors to the cell. */
  rect: { top: number; bottom: number; left: number }
}

/**
 * Click-to-expand detail for a single result cell: the FULL value (pretty-printed
 * when it is JSON), with a real copy-to-clipboard control — replacing the old,
 * uncopyable native `title` tooltip (IDE7). Anchored to the clicked cell, themed
 * entirely from tokens, dismissed by Escape or an outside click.
 * @param root0 - Component props.
 * @param root0.detail - The cell being expanded.
 * @param root0.onClose - Dismiss handler.
 * @returns The rendered popover.
 */
function CellDetailPopover({ detail, onClose }: { detail: CellDetail; onClose: () => void }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const expanded = useMemo(() => prettyCellValue(detail.raw), [detail.raw])

  // Escape closes — matches the IDE's modal/popover dismissal convention.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleCopy = useCallback(() => {
    void navigator.clipboard?.writeText(expanded.text).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      },
      () => {
        /* clipboard denied — ignore */
      },
    )
  }, [expanded.text])

  // Anchor below the cell, clamped to the viewport (no-op-safe in jsdom, where
  // the rect is all-zero and innerWidth defaults to 1024).
  const width = Math.min(520, (typeof window !== 'undefined' ? window.innerWidth : 520) - 32)
  const maxLeft = (typeof window !== 'undefined' ? window.innerWidth : 520) - width - 8
  const left = Math.max(8, Math.min(detail.rect.left, maxLeft))
  const top = detail.rect.bottom + 6

  return (
    <>
      {/* click-outside backdrop */}
      <div
        data-mol-id="database-cell-popover-backdrop"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 40 }}
      />
      <div
        role="dialog"
        aria-label={t('database.cellDetail', undefined, { defaultValue: 'Cell value' })}
        data-mol-id="database-cell-popover"
        style={{
          position: 'fixed',
          top,
          left,
          zIndex: 41,
          width,
          maxHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          background: 'var(--color-surface)',
          boxShadow: '0 8px 32px color-mix(in srgb, var(--color-foreground) 28%, transparent)',
          overflow: 'hidden',
        }}
      >
        <Flex
          align="center"
          gap="sm"
          style={{ padding: '7px 8px 7px 12px', borderBottom: '1px solid var(--color-border)' }}
        >
          <span
            data-mol-id="database-cell-column"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'var(--mol-font-mono, monospace)',
              color: 'var(--color-foreground)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {detail.column}
          </span>
          {expanded.isJson && (
            <span
              data-mol-id="database-cell-json-badge"
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                padding: '1px 6px',
                borderRadius: 4,
                color: 'var(--color-primary)',
                background: 'color-mix(in srgb, var(--color-primary) 15%, transparent)',
              }}
            >
              {t('database.cellJson', undefined, { defaultValue: 'JSON' })}
            </span>
          )}
          <Tooltip
            content={
              copied
                ? t('database.copied', undefined, { defaultValue: 'Copied' })
                : t('database.copyCell', undefined, { defaultValue: 'Copy value' })
            }
          >
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopy}
              data-mol-id="database-cell-copy"
              aria-label={t('database.copyCell', undefined, { defaultValue: 'Copy value' })}
            >
              <Icon name={copied ? 'check' : 'copy'} size={14} aria-hidden="true" />
            </Button>
          </Tooltip>
          <Tooltip content={t('database.closeCell', undefined, { defaultValue: 'Close' })}>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              data-mol-id="database-cell-close"
              aria-label={t('database.closeCell', undefined, { defaultValue: 'Close' })}
            >
              <Icon name="x-mark" size={16} aria-hidden="true" />
            </Button>
          </Tooltip>
        </Flex>
        <pre
          data-mol-id="database-cell-value"
          style={{
            margin: 0,
            padding: 12,
            overflow: 'auto',
            fontSize: 12,
            fontFamily: 'var(--mol-font-mono, monospace)',
            lineHeight: 1.5,
            color: 'var(--color-foreground)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {expanded.text}
        </pre>
      </div>
    </>
  )
}

/**
 * A SCOPED inline load error: the failure message plus a retry button for just
 * the part that failed (connection info / table list), so one half failing
 * never blanks the half that loaded.
 * @param root0 - Component props.
 * @param root0.molId - Automation id for the error line.
 * @param root0.retryMolId - Automation id for the retry button.
 * @param root0.onRetry - Re-runs just the failed load.
 * @param root0.children - The error message.
 * @returns The rendered inline error.
 */
function ScopedLoadError({
  molId,
  retryMolId,
  onRetry,
  children,
}: {
  molId: string
  retryMolId: string
  onRetry: () => void
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <Flex align="center" gap="sm" data-mol-id={molId} style={{ marginBottom: 10 }} wrap="wrap">
      <span style={{ fontSize: 13, color: 'var(--mol-color-error, #ef4444)' }}>{children}</span>
      <Button size="sm" variant="outline" onClick={onRetry} data-mol-id={retryMolId}>
        {t('database.retry', undefined, { defaultValue: 'Retry' })}
      </Button>
    </Flex>
  )
}

/**
 * Database panel — connection metadata, table browser, and read-only SQL
 * console.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose database to inspect.
 * @param root0.embedded - Drop the outer padding for in-row embedding.
 * @returns The rendered Database panel.
 */
export function DatabasePanel({ projectId, embedded }: DatabasePanelProps) {
  const isMobile = useIsMobile()
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()

  const [env, setEnv] = useState<OpsEnv>('dev')
  const [info, setInfo] = useState<DatabaseInfo | null>(null)
  const [tables, setTables] = useState<TableInfo[]>([])
  const [metaLoading, setMetaLoading] = useState(true)
  // Connection info and the table list load INDEPENDENTLY — when one fails the
  // other still renders, with a scoped inline error + retry for the failed
  // part (never one all-or-nothing "Failed to load" banner).
  const [infoError, setInfoError] = useState<string | null>(null)
  const [tablesError, setTablesError] = useState<string | null>(null)
  // An EXPECTED empty state (e.g. "Prod" clicked on a never-deployed project):
  // shown as a quiet note, NOT a red error banner. The inspector no longer
  // provisions a DB on a Prod click (IDE7), so this is the normal prod-empty UX.
  const [emptyNote, setEmptyNote] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  /**
   * True once a route has told us this caller's team role is too low for the
   * SQL console (admin+). It is a deliberate denial, so the console explains
   * it and stops offering Run — a viewer or editor used to get the raw string
   * "Access denied" painted into the red query-error box just for CLICKING A
   * TABLE NAME, since a table click auto-runs a preview query.
   */
  const [queryForbidden, setQueryForbidden] = useState(false)

  const [sql, setSql] = useState('')
  const [running, setRunning] = useState(false)
  const [grid, setGrid] = useState<ResultGrid | null>(null)
  // Whether the current `grid` came from a one-click table preview (a capped
  // `SELECT * … LIMIT n`) vs a hand-typed query — drives the limit indicator.
  const [gridIsPreview, setGridIsPreview] = useState(false)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [activeTable, setActiveTable] = useState<string | null>(null)
  // The result cell currently expanded in the click-to-expand copyable popover.
  const [cellDetail, setCellDetail] = useState<CellDetail | null>(null)

  // Load connection metadata for the current env (independent of the tables).
  const loadInfo = useCallback(async () => {
    try {
      const infoReq = databaseRequest(projectId, env)
      const infoRes = await http.get<DatabaseInfo>(infoReq.path, { params: infoReq.params })
      setInfo(infoRes.data ?? null)
      setInfoError(null)
      setEmptyNote(null)
    } catch (error) {
      setInfo(null)
      // A "no database yet" response (notably a Prod click with no deployment) is
      // an expected empty state, not a failure — show a quiet note, not a banner.
      const emptyKey = databaseEmptyStateKey(error)
      if (isInsufficientRoleError(error)) {
        // A deliberate denial — the panel says which role is needed instead of
        // painting the server's bare "Access denied" into a red box.
        setInfoError(null)
        setEmptyNote(
          t('database.forbidden', undefined, {
            defaultValue: 'Inspecting this database needs an admin role on this project.',
          }),
        )
      } else if (emptyKey === 'projectOps.error.sandboxNotRunning') {
        // Expected, with a next step — never a red banner.
        setInfoError(null)
        setEmptyNote(
          t('database.sandboxNotRunning', undefined, {
            defaultValue:
              'The sandbox is not running — start it to inspect the development database.',
          }),
        )
      } else if (emptyKey === 'projectOps.error.noProdDatabase') {
        setInfoError(null)
        setEmptyNote(
          t('database.noProdDatabase', undefined, {
            defaultValue:
              'No production database yet. Deploy this project to production to inspect its database.',
          }),
        )
      } else if (emptyKey === 'projectOps.error.noDatabase') {
        setInfoError(null)
        setEmptyNote(
          t('database.noDatabase', undefined, {
            defaultValue:
              'No database yet — Molecule provisions one automatically when your app first starts.',
          }),
        )
      } else {
        console.error('Failed to load database connection info', error)
        setInfoError(
          t('database.loadError', undefined, {
            defaultValue: 'Failed to load database connection.',
          }),
        )
      }
    }
  }, [http, projectId, env, t])

  // Load the table list for the current env (independent of the connection info).
  const loadTables = useCallback(async () => {
    try {
      const tablesReq = tablesRequest(projectId, env)
      const tablesRes = await http.get<TablesResponse>(tablesReq.path, {
        params: tablesReq.params,
      })
      setTables(tablesRes.data?.tables ?? [])
      setTablesError(null)
    } catch (error) {
      setTables([])
      if (databaseEmptyStateKey(error) || isInsufficientRoleError(error)) {
        // No database yet, sandbox asleep, or a role denial — the connection
        // side owns the quiet note; an empty table list is the honest
        // rendering here, not a red "Failed to list tables."
        setTablesError(null)
      } else {
        console.error('Failed to list database tables', error)
        setTablesError(
          t('database.tablesError', undefined, { defaultValue: 'Failed to list tables.' }),
        )
      }
    }
  }, [http, projectId, env, t])

  // Load both halves in parallel; each settles its own state, so a tables
  // failure never hides a loaded connection card (and vice versa).
  const loadMeta = useCallback(async () => {
    setMetaLoading(true)
    setEmptyNote(null)
    await Promise.all([loadInfo(), loadTables()])
    setMetaLoading(false)
  }, [loadInfo, loadTables])

  useEffect(() => {
    loadMeta()
  }, [loadMeta])

  // Run a read-only query against the project DB. `isPreview` records that the
  // result came from a one-click table preview (a capped `SELECT * … LIMIT n`),
  // so the limit indicator can fire even though the server-side `truncated` flag
  // never does at the small preview limit (IDE7).
  const runQuery = useCallback(
    async (rawSql: string, opts?: { isPreview?: boolean }) => {
      const text = rawSql.trim()
      if (!text || running) return
      setRunning(true)
      setQueryError(null)
      // Close any open cell popover — it references a now-stale grid.
      setCellDetail(null)
      try {
        const { path, body } = queryRequest(projectId, text, env)
        const res = await http.post<QueryResult>(path, body)
        setGrid(shapeResultGrid(res.data ?? null))
        setGridIsPreview(!!opts?.isPreview)
      } catch (err) {
        setGrid(null)
        setGridIsPreview(false)
        const { message, errorKey } = serverError(err)
        // A role denial is not a query failure: say which role is needed, and
        // remember it so the console stops inviting a click that cannot work.
        if (isInsufficientRoleError(err)) {
          setQueryForbidden(true)
          setActiveTable(null)
          setQueryError(
            t('database.queryForbidden', undefined, {
              defaultValue: 'Running queries needs an admin role on this project.',
            }),
          )
        } else if (errorKey === 'projectOps.error.sandboxNotRunning') {
          setQueryError(
            t('database.sandboxNotRunning', undefined, {
              defaultValue:
                'The sandbox is not running — start it to inspect the development database.',
            }),
          )
        } else if (errorKey === 'projectOps.error.readOnlyViolation') {
          setQueryError(
            t('database.readOnlyViolation', undefined, {
              defaultValue:
                'This database is read-only — write statements (INSERT/UPDATE/DELETE/DDL) are rejected.',
            }),
          )
        } else {
          setQueryError(
            message || t('database.queryError', undefined, { defaultValue: 'Query failed.' }),
          )
        }
      }
      setRunning(false)
    },
    [http, projectId, env, running, t],
  )

  const handleTableClick = useCallback(
    (name: string) => {
      const preview = tablePreviewQuery(name)
      setSql(preview)
      // Once we know queries are denied for this role, a table click fills the
      // console instead of firing a request that can only 403 again.
      if (queryForbidden) return
      setActiveTable(name)
      runQuery(preview, { isPreview: true })
    },
    [runQuery, queryForbidden],
  )

  // The row-cap notice for the current grid (server cap for a hand query, preview
  // cap for a one-click table preview). Memoized off the grid + preview flag.
  const rowCap = useMemo(() => rowCapNotice(grid, gridIsPreview), [grid, gridIsPreview])

  // Column → data-type hints for the results header. Only available when the
  // results came from a one-click table preview (activeTable); a hand-typed
  // query clears activeTable (below), so stale hints never show.
  const activeTableTypes = useMemo(
    () => columnTypeMap(tables.find((tbl) => tbl.name === activeTable)),
    [tables, activeTable],
  )

  const handleCopyUrl = useCallback(() => {
    const url = info?.maskedUrl
    if (!url) return
    void navigator.clipboard?.writeText(url).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      },
      () => {
        /* clipboard denied — ignore */
      },
    )
  }, [info])

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--color-foreground-secondary)',
    width: 70,
    flexShrink: 0,
  }
  const valueStyle: React.CSSProperties = {
    fontSize: 12,
    fontFamily: 'var(--mol-font-mono, monospace)',
    color: 'var(--mol-color-text, #e0e0e0)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }

  return (
    <div style={embedded ? undefined : { padding: 16 }} data-mol-id="database-panel">
      <Flex align="center" gap="sm" style={{ marginBottom: embedded ? 8 : 12 }} wrap="wrap">
        {/* Embedded inside a Storage row (which already carries the row
            label), the title drops to the shared 11px uppercase
            explorer-heading idiom so nothing sits at an odd size/indent. */}
        {embedded ? (
          <h4
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
              margin: 0,
              flex: 1,
              minWidth: 0,
            }}
          >
            {t('database.title', undefined, { defaultValue: 'Database' })}
          </h4>
        ) : (
          <h3
            className={cm.cn(cm.fontWeight('bold'))}
            style={{ fontSize: 15, margin: 0, flex: 1, minWidth: 0 }}
          >
            {t('database.title', undefined, { defaultValue: 'Database' })}
          </h3>
        )}

        {/* read-only badge — makes the contract obvious */}
        <span
          data-mol-id="database-readonly-badge"
          style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            padding: '2px 7px',
            borderRadius: 999,
            color: 'var(--color-foreground-secondary)',
            background: 'rgba(128,128,128,0.15)',
          }}
        >
          {t('database.readonly', undefined, { defaultValue: 'Read-only' })}
        </span>

        {/* dev / prod env toggle */}
        <Flex align="center" gap="xs" data-mol-id="database-env-toggle">
          {(['dev', 'prod'] as const).map((e) => {
            const isActive = env === e
            return (
              <button
                key={e}
                type="button"
                data-mol-id={`database-env-${e}`}
                aria-pressed={isActive}
                onClick={() => setEnv(e)}
                // mol-bespoke-button: segmented SELECTOR (aria-pressed) for
                // dev/prod, not a CTA — the shared, token-mixed
                // ACTIVE_PILL/INACTIVE_PILL treatment every IDE panel's filter
                // strip uses, with the compact touch floor added.
                className={cm.touchTargetCompact}
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'background 100ms, border-color 100ms',
                  ...(isActive ? ACTIVE_PILL : INACTIVE_PILL),
                }}
              >
                {e === 'dev'
                  ? t('database.env.dev', undefined, { defaultValue: 'Dev' })
                  : t('database.env.prod', undefined, { defaultValue: 'Prod' })}
              </button>
            )
          })}
        </Flex>
      </Flex>

      {metaLoading ? (
        <Flex align="center" justify="center" className={cm.sp('py', 6)}>
          <MoleculeSpinner />
        </Flex>
      ) : emptyNote ? (
        <div
          data-mol-id="database-empty-note"
          style={{
            padding: 12,
            fontSize: 13,
            color: 'var(--color-foreground-tertiary)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
          }}
        >
          {emptyNote}
        </div>
      ) : (
        <>
          {/* Connection metadata — a failure here is scoped: the table list
              and console below still render. */}
          {infoError && (
            <ScopedLoadError
              molId="database-error"
              retryMolId="database-info-retry"
              onRetry={() => void loadInfo()}
            >
              {infoError}
            </ScopedLoadError>
          )}
          {info && (
            <div
              data-mol-id="database-connection"
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <Flex align="center" gap="xs" style={{ marginBottom: 4 }}>
                <span style={labelStyle}>
                  {t('database.host', undefined, { defaultValue: 'Host' })}
                </span>
                <span style={valueStyle} data-mol-id="database-host">
                  {info.host}:{info.port}
                </span>
              </Flex>
              <Flex align="center" gap="xs" style={{ marginBottom: 4 }}>
                <span style={labelStyle}>
                  {t('database.name', undefined, { defaultValue: 'Database' })}
                </span>
                <span style={valueStyle} data-mol-id="database-name">
                  {info.database}
                </span>
              </Flex>
              <Flex align="center" gap="xs" style={{ marginBottom: 8 }}>
                <span style={labelStyle}>
                  {t('database.user', undefined, { defaultValue: 'User' })}
                </span>
                <span style={valueStyle} data-mol-id="database-user">
                  {info.user}
                </span>
              </Flex>
              <Flex align="center" gap="xs">
                <span style={labelStyle}>
                  {t('database.url', undefined, { defaultValue: 'URL' })}
                </span>
                <span style={{ ...valueStyle, flex: 1, minWidth: 0 }} data-mol-id="database-url">
                  {displayMaskedUrl(info)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyUrl}
                  data-mol-id="database-copy-url"
                >
                  {copied
                    ? t('database.copied', undefined, { defaultValue: 'Copied' })
                    : t('database.copy', undefined, { defaultValue: 'Copy' })}
                </Button>
              </Flex>
              <p
                data-mol-id="database-hosted-note"
                style={{
                  margin: '8px 0 0',
                  fontSize: 11,
                  color: 'var(--color-foreground-tertiary)',
                }}
              >
                {t('database.hostedByMolecule', undefined, {
                  defaultValue:
                    'Provisioned and hosted by Molecule automatically — dev and production.',
                })}
              </p>
              {/* WHO can dial this address. Both forms are correct for the app
                  and undialable from a laptop: the dev host is the sandbox's
                  own loopback (which on your machine is your machine), and the
                  prod host is a private cluster address. The card offered a
                  Copy button with no hint of either. */}
              <p
                data-mol-id="database-reachability-note"
                style={{
                  margin: '4px 0 0',
                  fontSize: 11,
                  color: 'var(--color-foreground-tertiary)',
                }}
              >
                {info.reachableFrom === 'sandbox'
                  ? t('database.reachableFromSandbox', undefined, {
                      defaultValue:
                        'This address works inside your sandbox, where your app runs. It is not reachable from your own machine.',
                    })
                  : t('database.reachableFromDeployment', undefined, {
                      defaultValue:
                        'This address works from your deployed app on Molecule’s private network. It is not reachable from your own machine.',
                    })}
              </p>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 11,
                  color: 'var(--color-foreground-tertiary)',
                }}
              >
                {t('database.passwordMasked', undefined, {
                  defaultValue: 'Password is masked. The full URL is never exposed here.',
                })}
              </p>
            </div>
          )}

          {/* Table list */}
          <h4
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
              margin: '0 0 8px',
            }}
          >
            {t('database.tables', undefined, { defaultValue: 'Tables' })}
          </h4>
          {tablesError ? (
            <ScopedLoadError
              molId="database-tables-error"
              retryMolId="database-tables-retry"
              onRetry={() => void loadTables()}
            >
              {tablesError}
            </ScopedLoadError>
          ) : tables.length === 0 ? (
            <div
              data-mol-id="database-tables-empty"
              style={{
                fontSize: 13,
                color: 'var(--color-foreground-tertiary)',
                marginBottom: 12,
              }}
            >
              {t('database.noTables', undefined, { defaultValue: 'No public tables yet.' })}
            </div>
          ) : (
            <Flex align="center" gap="xs" wrap="wrap" style={{ marginBottom: 12 }}>
              {tables.map((table) => {
                const isActive = activeTable === table.name
                return (
                  <Tooltip
                    key={table.name}
                    content={t(
                      'database.tableColumns',
                      { count: table.columns.length },
                      { defaultValue: `${table.columns.length} columns` },
                    )}
                  >
                    {/* mol-bespoke-button: table SELECTOR pill, not a CTA — one
                        per table name in a wrapping monospace list, styled by the
                        shared ACTIVE_PILL/INACTIVE_PILL token treatment. */}
                    <button
                      type="button"
                      data-mol-id={`database-table-${table.name}`}
                      onClick={() => handleTableClick(table.name)}
                      // Selector pill — bespoke by design, touch floor by contract.
                      className={cm.touchTargetCompact}
                      style={{
                        fontSize: 12,
                        padding: '3px 9px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontFamily: 'var(--mol-font-mono, monospace)',
                        ...(isActive ? ACTIVE_PILL : INACTIVE_PILL),
                      }}
                    >
                      {table.name}
                    </button>
                  </Tooltip>
                )
              })}
            </Flex>
          )}

          {/* Read-only SQL console */}
          <h4
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
              margin: '0 0 8px',
            }}
          >
            {t('database.console', undefined, { defaultValue: 'Read-only SQL console' })}
          </h4>
          <textarea
            data-mol-id="database-sql-input"
            value={sql}
            onChange={(e) => {
              setSql(e.target.value)
              // Editing the SQL by hand detaches it from the previewed table, so
              // drop the active-table highlight + its column type hints.
              if (activeTable) setActiveTable(null)
            }}
            placeholder={t('database.sqlPlaceholder', undefined, {
              defaultValue: 'SELECT * FROM … (read-only)',
            })}
            spellCheck={false}
            rows={3}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter runs the query.
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                runQuery(sql)
              }
            }}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'vertical',
              padding: '8px 10px',
              // ≥16px on phones so iOS Safari doesn't zoom the page on focus.
              fontSize: isMobile ? 16 : 12,
              fontFamily: 'var(--mol-font-mono, monospace)',
              lineHeight: 1.5,
              borderRadius: 6,
              border: '1px solid var(--color-border-secondary)',
              background: 'var(--color-surface)',
              color: 'var(--mol-color-text, #e0e0e0)',
              outline: 'none',
            }}
          />
          <Flex align="center" gap="sm" style={{ margin: '8px 0 12px' }}>
            <Button
              size="sm"
              loading={running}
              disabled={running || !sql.trim() || queryForbidden}
              onClick={() => runQuery(sql)}
              data-mol-id="database-run"
            >
              {t('database.run', undefined, { defaultValue: 'Run' })}
            </Button>
            <span style={{ fontSize: 11, color: 'var(--color-foreground-tertiary)' }}>
              {queryForbidden
                ? t('database.queryForbidden', undefined, {
                    defaultValue: 'Running queries needs an admin role on this project.',
                  })
                : t('database.runHint', undefined, {
                    defaultValue: 'Only read-only statements are allowed. ⌘/Ctrl+Enter to run.',
                  })}
            </span>
          </Flex>

          {queryError && (
            <div
              data-mol-id="database-query-error"
              style={{
                padding: 10,
                fontSize: 12,
                fontFamily: 'var(--mol-font-mono, monospace)',
                color: 'var(--mol-color-error, #ef4444)',
                border: '1px solid rgba(200,60,60,0.3)',
                background: 'rgba(200,60,60,0.08)',
                borderRadius: 6,
                marginBottom: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {queryError}
            </div>
          )}

          {/* Results grid */}
          {grid && (
            <div data-mol-id="database-results">
              {/* A result cut by SIZE can arrive with zero rows (one row bigger
                  than the whole budget). Saying "Query returned no rows." for
                  a query that returned data is the one thing this must not do,
                  so the cap notice renders above the empty state too. */}
              {grid.empty && rowCap && (
                <div
                  data-mol-id="database-truncated"
                  style={{ fontSize: 11, color: '#c8a000', marginBottom: 6 }}
                >
                  {t('database.sizeCapEmpty', undefined, {
                    defaultValue:
                      'The first row is larger than the result size limit, so nothing could be shown. Select fewer columns.',
                  })}
                </div>
              )}
              {/* Row-count / limit indicator — always shown so the result size
                  (and whether it hit the 100-row preview cap) is obvious. */}
              {!grid.empty && (
                <Flex
                  align="center"
                  justify="between"
                  gap="sm"
                  style={{ marginBottom: 6 }}
                  wrap="wrap"
                >
                  <span
                    data-mol-id="database-rowcount"
                    style={{ fontSize: 11, color: 'var(--color-foreground-tertiary)' }}
                  >
                    {t(
                      'database.rowCount',
                      { count: grid.rowCount },
                      { defaultValue: `${grid.rowCount} row(s)` },
                    )}
                  </span>
                  {rowCap && (
                    <span
                      data-mol-id="database-truncated"
                      style={{ fontSize: 11, color: '#c8a000' }}
                    >
                      {rowCap.kind === 'serverCap'
                        ? t(
                            'database.truncated',
                            { count: rowCap.rowCount, limit: rowCap.limit },
                            {
                              defaultValue: `Showing the first ${rowCap.rowCount} rows (server cap of ${rowCap.limit}). Refine your query to see more.`,
                            },
                          )
                        : rowCap.kind === 'sizeCap'
                          ? t(
                              'database.sizeCap',
                              { count: rowCap.rowCount },
                              {
                                defaultValue: `Showing the first ${rowCap.rowCount} rows — the result hit its size limit. Select fewer columns to see more.`,
                              },
                            )
                          : t(
                              'database.previewLimit',
                              { count: rowCap.rowCount, limit: rowCap.limit },
                              {
                                defaultValue: `Showing the first ${rowCap.rowCount} rows (table preview limit). Run a query to see more.`,
                              },
                            )}
                    </span>
                  )}
                </Flex>
              )}
              {grid.empty ? (
                <div
                  data-mol-id="database-no-rows"
                  style={{ fontSize: 13, color: 'var(--color-foreground-tertiary)' }}
                >
                  {t('database.noRows', undefined, { defaultValue: 'Query returned no rows.' })}
                </div>
              ) : (
                // Horizontal + vertical scroll; the table sizes to content (with
                // a min column width) so wide result sets scroll rather than crush.
                <div style={{ overflow: 'auto', maxHeight: 320 }}>
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      fontSize: 12,
                      fontFamily: 'var(--mol-font-mono, monospace)',
                      minWidth: '100%',
                    }}
                  >
                    <thead>
                      <tr>
                        {grid.columns.map((col) => {
                          const type = activeTableTypes[col]
                          return (
                            <th
                              key={col}
                              data-mol-id={`database-col-${col}`}
                              style={{
                                textAlign: 'left',
                                padding: '6px 12px',
                                borderBottom: '1px solid var(--color-border)',
                                color: 'var(--color-foreground-secondary)',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                position: 'sticky',
                                top: 0,
                                background: 'var(--color-surface)',
                                minWidth: 120,
                              }}
                            >
                              <div>{col}</div>
                              {type && (
                                <div
                                  data-mol-id={`database-coltype-${col}`}
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 400,
                                    textTransform: 'lowercase',
                                    color: 'var(--color-foreground-tertiary)',
                                  }}
                                >
                                  {type}
                                </div>
                              )}
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {grid.rows.map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          data-mol-id={`database-row-${rowIdx}`}
                          // Zebra (odd rows) + a row-hover highlight, both from the
                          // one ClassMap surface token — so data scanning is obvious
                          // (IDE7). The cells carry no inline background, so the
                          // row's themed tint shows through.
                          className={cm.cn(
                            cm.tableRowHoverable,
                            rowIdx % 2 === 1 && cm.tableRowStriped,
                          )}
                        >
                          {row.map((cell, colIdx) => (
                            <td
                              key={colIdx}
                              style={{
                                padding: 0,
                                borderBottom: '1px solid var(--color-border)',
                                minWidth: 120,
                                maxWidth: 360,
                              }}
                            >
                              {/* Click-to-expand: a real, keyboard-focusable button
                                  (not a hover-only native `title`) opens the copyable
                                  detail popover. Data renders in the PRIMARY
                                  foreground (was the muted secondary) — IDE7. */}
                              {/* mol-bespoke-button: a results-GRID CELL, not a
                                  CTA — it must render as monospace table text
                                  that happens to be clickable, never as a button
                                  box repeated across every cell of every row. */}
                              <button
                                type="button"
                                data-mol-id={`database-cell-${rowIdx}-${colIdx}`}
                                onClick={(e) =>
                                  setCellDetail({
                                    column: grid.columns[colIdx] ?? '',
                                    raw: cell,
                                    rect: e.currentTarget.getBoundingClientRect(),
                                  })
                                }
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '5px 12px',
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: 'pointer',
                                  fontSize: 12,
                                  fontFamily: 'var(--mol-font-mono, monospace)',
                                  color: 'var(--color-foreground)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {cell}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {cellDetail && <CellDetailPopover detail={cellDetail} onClose={() => setCellDetail(null)} />}
    </div>
  )
}
